import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';
import { BodyText, FallbackImage, theme, TitleText } from '@sg/garnish';

import { IllusLoyaltySignedOut } from '../../../assets';
import { LoyaltyIconSgReward } from '../../LoyaltyIconSgReward';

/**
 * Signed out state for loyalty in bag.
 */
export const BagLoyaltySignedOutCta = (props: BagLoyaltySignedOutCtaProps) => {
  const { message, imageUrl = '', onPress } = props;
  const { formatMessage } = useIntl();

  return (
    <Pressable
      style={styles.container}
      accessibilityRole="link"
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      onPress={onPress}
    >
      <FallbackImage
        contentFit="cover"
        style={styles.image}
        defaultImage={IllusLoyaltySignedOut}
        baseUrl={imageUrl}
        contentfulOptions={contentfulOptions}
        aria-label={formatMessage(messages.accessibilityLabel)}
      />

      <View style={styles.floatingContent}>
        <View style={styles.contentColumn}>
          <TitleText
            sizeMatch={['24']}
            style={styles.message}
            numberOfLines={4}
          >
            {message}
          </TitleText>
        </View>

        <View style={styles.sgRewardColumn}>
          <LoyaltyIconSgReward palette="white-outline" />

          <BodyText style={styles.message} sizeMatch={['22']}>
            {formatMessage(messages.rewards)}
          </BodyText>
        </View>
      </View>
    </Pressable>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const HEIGHT = 150;
const contentfulOptions = {
  fm: 'webp' as const,
  q: 90,
  h: HEIGHT,
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  rewards: {
    defaultMessage: 'rewards',
    description: 'Bag | Loyalty | Rewards',
  },
  accessibilityLabel: {
    defaultMessage: 'Create account to earn rewards',
    description: 'Bag | Loyalty | Signed Out A11y',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    height: HEIGHT,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.DARK,
  },
  image: {
    width: '100%',
    height: HEIGHT,
    borderRadius: theme.radius.large,
  },
  floatingContent: {
    position: 'absolute',
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: HEIGHT,
    padding: theme.spacing['4'],
  },
  contentColumn: {
    flex: 2,
  },
  sgRewardColumn: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
    gap: theme.spacing['1'],
  },
  message: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagLoyaltySignedOutCtaProps = {
  message: string;
  imageUrl?: string;
  onPress: () => void;
};
